import React, { useEffect, useState } from "react";
import axios from "axios";
import Avatar from "react-avatar";
import {
  Camera,
  Check,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { toastify } from "../../../components/Toast";
import "./PersonalDetails.scss";

const emptyProfile = {
  firstName: "",
  lastName: "",
  email: "",
  imageUrl: "",
};

function PersonalDetails() {
  const navigate = useNavigate();
  const [passwordPopup, setPasswordPopup] = useState(false);
  const [emailPopup, setEmailPopup] = useState(false);
  const [invitePopup, setInvitePopup] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPass, setCurrentPass] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [selectedProfile, setSelectedProfile] = useState("");
  const [chooseProfile, setChooseProfile] = useState(null);
  const [originalProfile, setOriginalProfile] = useState(emptyProfile);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      const token = localStorage.getItem("token");

      try {
        const response = await axios.get(`${process.env.REACT_APP_API}users/me`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const profile = response?.data?.data || {};
        const nextProfile = {
          firstName: profile?.firstName || "",
          lastName: profile?.lastName || "",
          email: profile?.email || "",
          imageUrl: profile?.imageUrl || "",
        };

        setFirstName(nextProfile.firstName);
        setLastName(nextProfile.lastName);
        setEmail(nextProfile.email);
        setSelectedProfile(nextProfile.imageUrl);
        setOriginalProfile(nextProfile);
      } catch (error) {
        if (error?.code !== "ERR_CANCELED") {
          console.error("Unable to load profile:", error);
        }
      }
    };

    fetchData();
    return () => controller.abort();
  }, []);

  const resetSensitiveFields = () => {
    setCurrentPass("");
    setNewPassword("");
    setConfirmPass("");
    setNewEmail("");
  };

  const closePasswordPopup = () => {
    setPasswordPopup(false);
    resetSensitiveFields();
  };

  const closeEmailPopup = () => {
    setEmailPopup(false);
    resetSensitiveFields();
  };

  const closeInvitePopup = () => setInvitePopup(false);

  const passwordRules = [
    {
      id: "length",
      label: "Between 8 and 64 characters",
      isValid: newPassword.length >= 8 && newPassword.length <= 64,
    },
    {
      id: "uppercase",
      label: "At least one uppercase letter",
      isValid: /[A-Z]/.test(newPassword),
    },
    {
      id: "number",
      label: "At least one number",
      isValid: /\d/.test(newPassword),
    },
    {
      id: "special",
      label: "At least one special character",
      isValid: /[^A-Za-z0-9\s]/.test(newPassword),
    },
  ];
  const passwordMeetsRequirements = passwordRules.every((rule) => rule.isValid);
  const passwordsMatch = Boolean(confirmPass) && newPassword === confirmPass;

  const canUpdatePassword = Boolean(
    currentPass && passwordMeetsRequirements && passwordsMatch
  );
  const canUpdateEmail = Boolean(currentPass && newEmail.trim());
  const canSaveProfile = Boolean(firstName.trim() && lastName.trim());

  const updateEmail = async (event) => {
    event.preventDefault();
    if (!canUpdateEmail || isUpdatingEmail) return;

    const token = localStorage.getItem("token");
    setIsUpdatingEmail(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}users/chnage-emailAddress`,
        { currentPassword: currentPass, email: newEmail.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmail(newEmail.trim());
      setOriginalProfile((profile) => ({ ...profile, email: newEmail.trim() }));
      toastify("success", response?.data?.message || "Email address updated.");
      closeEmailPopup();
    } catch (error) {
      console.error("Unable to update email:", error);
      toastify("error", error?.response?.data?.message || "Unable to update email address.");
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const updatePassword = async (event) => {
    event.preventDefault();
    if (!canUpdatePassword || isUpdatingPassword) return;

    const token = localStorage.getItem("token");
    setIsUpdatingPassword(true);
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}users/password-update`,
        { currentPassword: currentPass, password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response?.data?.message || "Password updated.");
      closePasswordPopup();
    } catch (error) {
      console.error("Unable to update password:", error);
      toastify("error", error?.response?.data?.message || "Unable to update password.");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const saveProfileData = async () => {
    if (!canSaveProfile || isSavingProfile) return;

    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("firstName", firstName.trim());
    formData.append("lastName", lastName.trim());
    if (chooseProfile) formData.append("profileImage", chooseProfile);

    setIsSavingProfile(true);
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}users/profile-update`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFirstName(firstName.trim());
      setLastName(lastName.trim());
      setOriginalProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email,
        imageUrl: selectedProfile,
      });
      setChooseProfile(null);
      toastify("success", response?.data?.message || "Profile updated.");
    } catch (error) {
      console.error("Unable to update profile:", error);
      toastify("error", error?.response?.data?.message || "Unable to update profile.");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const deleteUserAccount = async () => {
    if (isDeletingAccount) return;

    const token = localStorage.getItem("token");
    setIsDeletingAccount(true);
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API}users/delete-userAccount`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response?.data?.message || "Account deleted.");
      localStorage.removeItem("userData");
      localStorage.removeItem("token");
      localStorage.removeItem("settingsToken");
      localStorage.removeItem("cardFilter");
      localStorage.removeItem("bucketFilter");
      localStorage.removeItem("bucketView");
      navigate("/");
    } catch (error) {
      console.error("Unable to delete account:", error);
      toastify("error", error?.response?.data?.message || "Unable to delete account.");
      setIsDeletingAccount(false);
    }
  };

  const handleImageSelect = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toastify("error", "Choose a valid image file.");
      return;
    }

    setChooseProfile(file);
    const reader = new FileReader();
    reader.onload = () => setSelectedProfile(String(reader.result || ""));
    reader.onerror = () => toastify("error", "Unable to preview this image.");
    reader.readAsDataURL(file);
  };

  const resetProfileChanges = () => {
    setFirstName(originalProfile.firstName);
    setLastName(originalProfile.lastName);
    setSelectedProfile(originalProfile.imageUrl);
    setChooseProfile(null);
  };

  return (
    <div className="personaldetails-section personal-details-page">
      <ToastContainer />

      <section className="profile-settings-card" aria-labelledby="profile-details-title">
        <header className="profile-card-header">
          <span className="profile-card-icon"><UserRound size={20} /></span>
          <div>
            <h2 id="profile-details-title">Profile details</h2>
            <p>Update your photo and the name shown across Zimzee.</p>
          </div>
        </header>

        <div className="profile-photo-row">
          <div className="profile-avatar-frame">
            {selectedProfile ? (
              <img src={selectedProfile} alt={`${firstName || "User"} profile`} />
            ) : (
              <Avatar name={`${firstName} ${lastName}`.trim() || "User"} size="112" round />
            )}
          </div>
          <div className="profile-photo-actions">
            <label className="profile-button profile-button--secondary" htmlFor="profile-picture-input">
              <Camera size={17} />
              <span>Upload new picture</span>
            </label>
            <input
              id="profile-picture-input"
              className="profile-file-input"
              type="file"
              name="profilePicture"
              accept="image/*"
              onChange={handleImageSelect}
            />
            <p>Use a square JPG, PNG, or WebP image.</p>
          </div>
        </div>

        <div className="profile-form-grid">
          <label className="profile-field">
            <span>First name</span>
            <input
              type="text"
              name="firstName"
              value={firstName}
              autoComplete="given-name"
              onChange={(event) => setFirstName(event.target.value)}
              required
            />
          </label>
          <label className="profile-field">
            <span>Last name</span>
            <input
              type="text"
              name="lastName"
              value={lastName}
              autoComplete="family-name"
              onChange={(event) => setLastName(event.target.value)}
              required
            />
          </label>
        </div>

        <footer className="profile-card-footer">
          <button type="button" className="profile-button profile-button--ghost" onClick={resetProfileChanges}>
            Cancel
          </button>
          <button
            type="button"
            className="profile-button profile-button--primary"
            onClick={saveProfileData}
            disabled={!canSaveProfile || isSavingProfile}
          >
            {isSavingProfile ? "Saving…" : "Save changes"}
          </button>
        </footer>
      </section>

      <section className="profile-settings-card" aria-labelledby="account-security-title">
        <header className="profile-card-header">
          <span className="profile-card-icon"><ShieldCheck size={20} /></span>
          <div>
            <h2 id="account-security-title">Account security</h2>
            <p>Manage the email and password used to access your account.</p>
          </div>
        </header>

        <div className="profile-setting-row">
          <span className="profile-setting-icon"><Mail size={19} /></span>
          <div className="profile-setting-copy">
            <strong>Email address</strong>
            <span>{email || "No email address available"}</span>
          </div>
          <button type="button" className="profile-button profile-button--secondary" onClick={() => setEmailPopup(true)}>
            Change email
          </button>
        </div>

        <div className="profile-setting-row">
          <span className="profile-setting-icon"><LockKeyhole size={19} /></span>
          <div className="profile-setting-copy">
            <strong>Password</strong>
            <span>Set a secure password for your account.</span>
          </div>
          <button type="button" className="profile-button profile-button--secondary" onClick={() => setPasswordPopup(true)}>
            Change password
          </button>
        </div>
      </section>

      <section className="profile-settings-card profile-danger-card" aria-labelledby="delete-account-title">
        <header className="profile-card-header">
          <span className="profile-card-icon profile-card-icon--danger"><Trash2 size={20} /></span>
          <div>
            <h2 id="delete-account-title">Delete account</h2>
            <p>Permanently remove your account and all associated data.</p>
          </div>
        </header>
        <div className="profile-danger-action">
          <p>This action cannot be undone. Export anything you want to keep before continuing.</p>
          <button type="button" className="profile-button profile-button--danger" onClick={() => setInvitePopup(true)}>
            Delete my account
          </button>
        </div>
      </section>

      {passwordPopup && (
        <div className="profile-modal-backdrop" onMouseDown={closePasswordPopup}>
          <form
            className="profile-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="change-password-title"
            onSubmit={updatePassword}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <h3 id="change-password-title">Change password</h3>
                <p>Create a strong password that meets every requirement below.</p>
              </div>
              <button type="button" className="profile-modal-close" onClick={closePasswordPopup} aria-label="Close">
                <X size={20} />
              </button>
            </header>
            <div className="profile-modal-body">
              <label className="profile-field">
                <span>Current password</span>
                <input type="password" value={currentPass} autoComplete="current-password" onChange={(event) => setCurrentPass(event.target.value)} required />
              </label>
              <label className="profile-field">
                <span>New password</span>
                <input type="password" value={newPassword} maxLength={64} autoComplete="new-password" onChange={(event) => setNewPassword(event.target.value)} required />
              </label>
              <label className="profile-field">
                <span>Confirm new password</span>
                <input type="password" value={confirmPass} maxLength={64} autoComplete="new-password" onChange={(event) => setConfirmPass(event.target.value)} required />
              </label>
              <div className="profile-password-rules" aria-live="polite">
                {passwordRules.map((rule) => (
                  <div className={rule.isValid ? "is-valid" : ""} key={rule.id}>
                    <span className="profile-password-rule-icon" aria-hidden="true">
                      {rule.isValid ? <Check size={13} strokeWidth={3} /> : null}
                    </span>
                    <span>{rule.label}</span>
                  </div>
                ))}
                <div className={passwordsMatch ? "is-valid" : ""}>
                  <span className="profile-password-rule-icon" aria-hidden="true">
                    {passwordsMatch ? <Check size={13} strokeWidth={3} /> : null}
                  </span>
                  <span>Passwords match</span>
                </div>
              </div>
            </div>
            <footer>
              <button type="button" className="profile-button profile-button--ghost" onClick={closePasswordPopup}>Cancel</button>
              <button type="submit" className="profile-button profile-button--primary" disabled={!canUpdatePassword || isUpdatingPassword}>
                {isUpdatingPassword ? "Updating…" : "Change password"}
              </button>
            </footer>
          </form>
        </div>
      )}

      {emailPopup && (
        <div className="profile-modal-backdrop" onMouseDown={closeEmailPopup}>
          <form
            className="profile-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="change-email-title"
            onSubmit={updateEmail}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <h3 id="change-email-title">Change email address</h3>
                <p>Confirm the change with your current password.</p>
              </div>
              <button type="button" className="profile-modal-close" onClick={closeEmailPopup} aria-label="Close">
                <X size={20} />
              </button>
            </header>
            <div className="profile-modal-body">
              <label className="profile-field">
                <span>New email address</span>
                <input type="email" value={newEmail} autoComplete="email" onChange={(event) => setNewEmail(event.target.value)} required />
              </label>
              <label className="profile-field">
                <span>Current password</span>
                <input type="password" value={currentPass} autoComplete="current-password" onChange={(event) => setCurrentPass(event.target.value)} required />
              </label>
            </div>
            <footer>
              <button type="button" className="profile-button profile-button--ghost" onClick={closeEmailPopup}>Cancel</button>
              <button type="submit" className="profile-button profile-button--primary" disabled={!canUpdateEmail || isUpdatingEmail}>
                {isUpdatingEmail ? "Updating…" : "Change email"}
              </button>
            </footer>
          </form>
        </div>
      )}

      {invitePopup && (
        <div className="profile-modal-backdrop" onMouseDown={closeInvitePopup}>
          <div
            className="profile-modal profile-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-delete-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <h3 id="confirm-delete-title">Delete your account?</h3>
                <p>This action is permanent and cannot be reversed.</p>
              </div>
              <button type="button" className="profile-modal-close" onClick={closeInvitePopup} aria-label="Close">
                <X size={20} />
              </button>
            </header>
            <div className="profile-modal-body">
              <div className="profile-delete-warning">
                All of your Zimzee data and content will be permanently removed.
              </div>
            </div>
            <footer>
              <button type="button" className="profile-button profile-button--ghost" onClick={closeInvitePopup}>Cancel</button>
              <button type="button" className="profile-button profile-button--danger" onClick={deleteUserAccount} disabled={isDeletingAccount}>
                {isDeletingAccount ? "Deleting…" : "Delete account"}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default PersonalDetails;
